import React, { useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { selectSession } from '@core/store/modules/session/sessionSelectors';
import { useLanguage } from 'Context/LanguageContext';
import RestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	SmartActionAccentBar,
	SmartActionLabel,
	SmartActionPaper,
	SmartActionStack,
	SmartCloseFloatingButton,
	SmartFloatingPopupContainer
} from '~/FielderElements/SmartTaskTable/SmartStyles';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import { FielderHard } from '@core/theme/baseTheme';
import { keyframes } from '@mui/system';

const SmartFielderActionPopup = (
	{
		selectedRows = [], selectedCount = 0, disabledAction, restoreAction, closeAction, deleteAction
	}) => {
	const lan = useLanguage();
	const dispatch = useDispatch();
	const session = useSelector(selectSession);
	const fielderMsg = useFielderSnackBar();
	const actions = useMemo(() => {
		return [
			{
				icon: RestoreIcon,
				label: lan.restore || 'Restore',
				onClick: () => restoreAction?.(selectedRows),
				show: !!restoreAction,
			},
			{
				icon: DeleteIcon,
				label: lan.deactivate || 'Disable',
				onClick: () => disabledAction?.(selectedRows),
				show: !!disabledAction,
			},
			{
				icon: DeleteIcon,
				label: lan.delete || 'Delete',
				onClick: () => deleteAction?.(selectedRows),
				show: !!deleteAction,
			},
		];
	}, [selectedRows, restoreAction, disabledAction, deleteAction, lan.restore, lan.deactivate, lan.delete]);
	const bounceOnce =
		keyframes` 0%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } 60% { transform: translateY(0); } `;
	const ActionButton = useCallback(({ icon: Icon, label, onClick }) => {
		const [playBounce, setPlayBounce] = React.useState(false);
		const [hasBounced, setHasBounced] = React.useState(false);
		const handleMouseEnter = () => {
			if (!hasBounced) {
				setPlayBounce(true);
				setHasBounced(true);
				setTimeout(() => setPlayBounce(false), 500);
			}
		};
		return (
			<Tooltip title={label} arrow>
				<SmartActionStack spacing={0.25} alignItems="center">
					<IconButton
						size="small"
						onClick={onClick}
						onMouseEnter={handleMouseEnter}
						sx={{
							p: 0,
							'& .action-icon': {
								color: 'text.disabled',
								transition: 'transform 160ms ease, color 160ms ease',
							},
							'&:hover .action-icon': {
								color: FielderHard,
								transform: 'translateY(-1px) scale(1.06)',
							},
							animation: playBounce ? `${bounceOnce} 450ms ease-out 1` : 'none',
						}}
					>
						<Icon fontSize="small" className="action-icon" />
					</IconButton>
					<SmartActionLabel variant="caption" noWrap>
						{label}
					</SmartActionLabel>
				</SmartActionStack>
			</Tooltip>
		);
	}, []);

	return (
		<SmartFloatingPopupContainer>
			<SmartActionPaper elevation={3}>
				<Box sx={{ display: 'flex', alignItems: 'center', mr: 0 }}>
					<SmartActionAccentBar />
				</Box>
				<Typography variant="h5" sx={{ color: '#f8445b', lineHeight: 1, mr: 0 }}>
					{selectedCount}
				</Typography>
				<Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1, textAlign: 'center' , width: '50%', m: 1 }}>
					{selectedCount === 1 ? lan.selectedElement : lan.selectedElements}
				</Typography>
				{actions.filter(a => a.show).map((action, idx) => (
					<ActionButton key={idx} {...action} />
				))}
				<SmartCloseFloatingButton size="small" onClick={closeAction}>
					<CloseIcon fontSize="small" />
				</SmartCloseFloatingButton>
			</SmartActionPaper>
		</SmartFloatingPopupContainer>
	);
};

export default React.memo(SmartFielderActionPopup);
